import { useEffect, useReducer, useRef } from "react";
import { useRouteMatch } from "react-router-dom";
import OrganisationService from "../services/organisation-service";
import { hasSyncedLastRefreshDate } from "../util/organisation";
import { useCacheActions } from "./useFetch";
import usePush from "./usePush";
import useOnUpdate from "./useOnUpdate";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
export const ORG_SYNC_COMPLETED_EVENT = "chaser:organisation-sync-completed";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function onVisibilityChange(cb) {
  function update() {
    cb(document.visibilityState === "visible");
  }
  document.addEventListener("visibilitychange", update);
  return () => document.removeEventListener("visibilitychange", update);
}

function emitOrganisationSyncCompleted({ shortCode, source }) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;

  window.dispatchEvent(
    new CustomEvent(ORG_SYNC_COMPLETED_EVENT, {
      detail: {
        shortCode,
        source,
        timestamp: Date.now(),
      },
    }),
  );
}

function useOrganisationSync({
  shortCode,
  isLoggedIn,
  hasOrganisations,
  expiredToken,
  lastRefreshDate,
}) {
  const orgMatch = useRouteMatch("/o/:shortCode");
  const isOrganisationRoute = !!orgMatch;

  const [, forceRender] = useReducer((s) => s + 1, 0);

  const { invalidateDomain } = useCacheActions();
  const syncAction = usePush(async (...args) => {
    const [syncShortCode, automaticFirstSync] = args;

    try {
      const res = await OrganisationService.sync(...args);

      emitOrganisationSyncCompleted({
        shortCode: syncShortCode,
        source: automaticFirstSync ? "automatic" : "manual",
      });

      return res;
    } finally {
      invalidateDomain("organisation");
    }
  });

  useEffect(
    () => onVisibilityChange((visible) => visible && forceRender()),
    [],
  );

  const triggeredShortCodeRef = useRef(null);

  const maybeAutoSync = () => {
    if (
      isOrganisationRoute ||
      !hasOrganisations ||
      expiredToken ||
      !shortCode ||
      !isLoggedIn
    )
      return;

    const lastSync = lastRefreshDate ? new Date(lastRefreshDate).getTime() : 0;
    if (Date.now() - lastSync < ONE_DAY_MS) return;

    if (triggeredShortCodeRef.current === shortCode) return;
    triggeredShortCodeRef.current = shortCode;

    (async () => {
      await syncAction.execute(shortCode, true);
      await wait(3000);
      syncAction.reset();
    })();
  };

  useOnUpdate(maybeAutoSync, [isLoggedIn]);

  useEffect(() => {
    if (hasSyncedLastRefreshDate(lastRefreshDate)) return;

    maybeAutoSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortCode, lastRefreshDate]);

  return syncAction;
}

export default useOrganisationSync;
